import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { MeasurementDatesService } from 'api';
import {
  MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE,
  MEASUREMENT_DATES_BY_FIRM_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import {
  GetMeasurementDatesByFirmParams,
  GetMeasurementDatesByFirmResponse,
  GetMeasurementDatesByFirmReturn,
  UseGetMeasurementDatesByFirmParams,
} from './useGetMeasurementDatesByFirm.types';

export const getMeasurementDatesByFirm = async (
  params: GetMeasurementDatesByFirmParams
): Promise<GetMeasurementDatesByFirmResponse | null> => {
  const { firmId } = params;

  if (isNil(firmId)) {
    return throwError(MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const measurementDates = await MeasurementDatesService.apiMeasurementDatesAllCompaniesByFirmRead(toString(firmId));

    if (!measurementDates) {
      return throwError(MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE);
    }

    return measurementDates;
  } catch (error) {
    return throwError(MEASUREMENT_DATES_BY_FIRM_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetMeasurementDatesByFirm = (params: UseGetMeasurementDatesByFirmParams): GetMeasurementDatesByFirmReturn => {
  const { firmId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetMeasurementDatesByFirmReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getMeasurementDatesByFirm({ firmId }),
    queryKey: [MEASUREMENT_DATES_BY_FIRM_QUERY_KEY, firmId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetMeasurementDatesByFirm;
